import type { FunctionComponent } from 'react';
import { useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { config } from '../../../../config/config';
import { ContextNavigate } from '../../../shared/components/context-navigate/context-navigate';
import { useToast } from '../../../shared/hooks/use-toast';
import { useVerifyEmailMutation } from '../../queries/verify.graphql';

const NOTIFICATION_DELAY = 8000;

export const Verification: FunctionComponent = () => {
    const { profileID, token } = useParams();
    const [{ error, fetching }, execute] = useVerifyEmailMutation();
    const hasSentVerification = useRef(false);
    const toast = useToast();
    const intl = useIntl();

    useEffect(() => {
        // Effect used as per exceptions in https://beta.reactjs.org/learn/you-might-not-need-an-effect
        if (hasSentVerification.current) {
            return;
        }

        if (!token || !profileID) {
            return;
        }

        const send = async () => {
            hasSentVerification.current = true;

            try {
                const { error } = await execute({
                    verifyInput: {
                        code: token,
                        type: 'email',
                    },
                    userProfileId: profileID,
                    xTenantId: config.TENANT_ID,
                });

                if (error) {
                    throw error;
                }

                toast.success(
                    intl.formatMessage({
                        id: 'verification.email.success',
                        defaultMessage: 'Your email has been verified. You may now login!',
                    }),
                    { duration: NOTIFICATION_DELAY },
                );
            } catch (error) {
                toast.error(
                    intl.formatMessage({
                        id: 'verification.email.error',
                        defaultMessage:
                            'We could not verify your email. You either have already verified it or the link is invalid.',
                    }),
                    { duration: NOTIFICATION_DELAY },
                );
            }
        };

        send();
    }, [token, execute, profileID, toast, intl]);

    if (!token || !profileID) {
        return <ContextNavigate to="/404" />;
    }

    /**
     * TODO: #116 What should we return while we wait for verification?
     */
    if (fetching) {
        return <>...</>;
    }

    if (error) {
        return <ContextNavigate to={'/404'} />;
    }

    return <ContextNavigate to={'/sports'} />;
};
